import { Injectable, inject, signal, computed } from '@angular/core';
import { isCalComBookingEligible } from './booking.util';
import { ApiService } from './api.service';
import { SessionService } from './session.service';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';
import { resolveMatchmakingEventIdFromState } from 'src/app/@utils/matchmaking/resolve-matchmaking-event-id';

/* ============================================================================
   Endpoint map — single source of truth. AWS-blocked routes (show-winners /
   event-backend / networking) are confirmed in AWS_SETUP.md; when the real
   paths arrive this is the only place to change. Every read degrades to a safe
   empty value so the UI can show an honest "not available yet" state instead
   of a blank screen.
   ========================================================================== */
export const ENDPOINTS = {
  // ── show-winners POC (LIVE on Vercel; legacy x-user-email/x-user-id/x-event-id) ──
  events: (expoId: string) => `/api/events/${encodeURIComponent(expoId)}`,
  eventExhibitors: (expoId: string) =>
    `/api/events/${encodeURIComponent(expoId)}/exhibitors`,
  attendee: (id: string) => `/api/demo/attendees/${encodeURIComponent(id)}`,
  // Attendee flow (verified contract): register → chat/init → chat {message} → matches.
  register: '/api/attendee/register',
  chatInit: '/api/attendee/chat/init',
  sessions: '/api/sessions',
  matches: '/api/attendee/matches',
  matchesGenerate: '/api/attendee/matches/generate',
  chat: '/api/attendee/chat',
  enrich: '/api/attendee/enrich',
  profile: '/api/attendee/profile',
  matchAction: (id: string) => `/api/matches/${encodeURIComponent(id)}/action`,
  liveState: (sessionId: string) =>
    `/api/sessions/${encodeURIComponent(sessionId)}/live`,
  // ── auth (Cognito CUSTOM_AUTH OTP — converge, pending) ──
  sendOtp: '/auth/send-otp',
  verifyOtp: '/auth/verify-otp',
  resolve: '/auth/resolve',
  // ── cross-app networking (new, DynamoDB-backed — framework, pending) ──
  requests: '/api/networking/requests',
  connections: '/api/networking/connections',
} as const;

/* ── view models ─────────────────────────────────────────────────────────── */

/** Normalized match/exhibitor card shape consumed by Home + Network UI. */
export interface SocialLinks {
  linkedin?: string;
  facebook?: string;
  instagram?: string;
  youtube?: string;
}

/** Normalized exhibitor row for the Exhibitors directory (distinct from MatchView). */
export interface ExhibitorDirectoryView {
  id: string;
  name: string;
  booth: string | null;
  description: string;
  /** Short tile descriptor (capabilities / categories). */
  tagline: string;
  website: string;
  domain: string;
  logo: string;
  initials: string;
  booking: string;
  bookingProvider: string;
  socials?: SocialLinks;
  productsServices: string[];
  companySummary: string;
  capabilities: string[];
  categories: string[];
  audience: string[];
  contactEmail?: string;
  enriched: boolean;
}

export interface MeetingDetailsView {
  status: string | null;
  startTime: string | null;
  endTime: string | null;
  attendeeTimeZone?: string;
  isVirtual?: boolean;
  locationType?: string;
  joinUrl?: string;
  title?: string;
}

export interface MatchView {
  id: string;
  matchId: string | null;
  /** Backend lifecycle: active | booked_meeting | no_interest | … */
  matchStatus: string | null;
  name: string;
  company: string;
  tagline: string;
  reason: string;
  confidenceTier: string | null;
  matchScore: number;
  capabilities: string[];
  booth: string | null;
  website: string;
  booking: string;
  bookingProvider: string;
  logo: string;
  domain: string;
  initials: string;
  /** Exhibitor marketing blurb (exhibitor.description). */
  description: string;
  /** Longer prose blurb for the booth profile (enrichment / structured_data). */
  companySummary: string;
  /** Macro categories the exhibitor sits in. */
  categories: string[];
  /** Who the exhibitor sells to. */
  audience: string[];
  productsServices: string[];
  contactEmail?: string;
  /** Present when a meeting exists or is being booked. */
  meetingDetails: MeetingDetailsView | null;
  /** Public social links (exhibitor.metadata.*), shown on the booth profile. */
  socials?: SocialLinks;
}

/** Map a directory row to the shared profile view (no match context). */
export function directoryToProfileView(e: ExhibitorDirectoryView): MatchView {
  return {
    id: e.id,
    matchId: null,
    matchStatus: null,
    name: e.name,
    company: e.name,
    tagline: e.tagline,
    description: e.description,
    reason: '',
    confidenceTier: null,
    matchScore: 0,
    capabilities: e.capabilities,
    booth: e.booth,
    website: e.website,
    booking: e.booking,
    bookingProvider: e.bookingProvider,
    logo: e.logo,
    domain: e.domain,
    initials: e.initials,
    companySummary: e.companySummary,
    categories: e.categories,
    audience: e.audience,
    productsServices: e.productsServices,
    contactEmail: e.contactEmail,
    meetingDetails: null,
    socials: e.socials,
  };
}

export interface MatchesFetchResult {
  matches: MatchView[];
  activeCount: number;
  totalCount: number;
}

export interface AttendeeProfile {
  id: string;
  name: string;
  company: string;
  role: string;
  enriched: boolean;
  summary: string;
  painPoints: string[];
  solutionRequirements: string[];
  interests: string[];
}

export interface EventConfig {
  expoId: string;
  name: string;
  organizer: string;
  venue: string;
  city: string;
  startDate: string | null;
  endDate: string | null;
  status: 'upcoming' | 'live' | 'ended' | 'unknown';
}

export interface SessionView {
  id: string;
  title: string;
  track: string;
  room: string;
  speakers: string[];
  start: string | null;
  end: string | null;
  state: 'past' | 'live' | 'upcoming';
  whyMatch: string;
  summary: string;
}

export interface ConnectionView {
  id: string;
  exhibitorId: string;
  name: string;
  company: string;
  tagline: string;
  logo: string;
  initials: string;
  status: 'pending' | 'active' | 'rejected';
  slot: string;
  note: string;
}

/** One assistant turn from the preferences intake chat (`/api/attendee/chat`).
 *  Tolerant of the various field names a conversational endpoint may use, so the
 *  UI renders the assistant's real questions (never a script we invented). */
export interface ChatTurn {
  /** Assistant's next message to show in the feed. */
  message: string;
  /** Optional quick-reply chips the API suggests for the current question. */
  suggestions: string[];
  /** True when the API signals the intake is complete → commit + enrich. */
  done: boolean;
}

/* ── service ─────────────────────────────────────────────────────────────── */

@Injectable({ providedIn: 'root' })
export class BackendService {
  // ── public state (signals read by Home / Network / Profile + the TopBar) ──
  /** Shared event config — fetched live, read by every screen + the TopBar. */
  public readonly eventConfig = signal<EventConfig | null>(null);
  /** Shared attendee profile + enrichment status. Drives the enriched-vs-intake
   *  branching on Home / Network / Profile. Fetched live (register), not stored. */
  public readonly attendeeProfile = signal<AttendeeProfile | null>(null);
  public readonly profileLoaded = signal(false);
  /** True only once the real profile says the attendee is enriched. */
  public readonly enriched = computed(
    () => this.attendeeProfile()?.enriched ?? false
  );
  /** The exhibitor match currently open on the profile page (set before navigating). */
  public readonly viewedMatch = signal<MatchView | null>(null);

  // ── private state ──
  private readonly _api = inject(ApiService);
  private readonly _session = inject(SessionService);
  private readonly _eventConfigState = inject(EventConfigStateService);
  /** In-memory match cache (RAM only — NEVER persisted, so every page load
   *  re-fetches live). Keeps Home/Network consistent within a single view. */
  private _matchCache: MatchesFetchResult | null = null;
  private _matchPromise: Promise<MatchesFetchResult> | null = null;
  private _exhibitorCache = new Map<string, ExhibitorDirectoryView[]>();
  private _exhibitorPromise = new Map<
    string,
    Promise<ExhibitorDirectoryView[]>
  >();
  private _eventPromise: Promise<EventConfig | null> | null = null;
  private _profilePromise: Promise<AttendeeProfile | null> | null = null;

  private get userId(): string {
    const id = this._session.userId();
    return !id || id === 'preview' ? '' : id;
  }
  /** Event scope (event_id / x-event-id) for show-winners attendee routes.
   *  Prefer event config EventIdentifier (e.g. TWE2026), then session/enroll. */
  private get eventId(): string {
    return this.expoId;
  }

  public get expoId(): string {
    return resolveMatchmakingEventIdFromState(
      this._eventConfigState,
      this._session.session()?.expoId
    );
  }

  public get configured(): boolean {
    return this._api.configured;
  }

  // ── matchmaking ──────────────────────────────────────────────────────────
  /** Fetch the attendee's match deck — live on every load (RAM cache only, NEVER
   *  persisted), so the deck always reflects the backend. `force` bypasses both the
   *  in-flight de-dupe and the RAM cache. */
  async getMatches(
    opts: { force?: boolean } = {}
  ): Promise<MatchesFetchResult> {
    if (!opts.force) {
      if (this._matchCache) return this._matchCache;
      if (this._matchPromise) return this._matchPromise;
    }

    const attempt = async (tries: number): Promise<MatchesFetchResult> => {
      const r = await this._api.post<{
        matches?: unknown[];
        active_count?: number;
        total_count?: number;
      }>(ENDPOINTS.matches, {});
      const ms = (r?.matches ?? []).map((m, i) => adaptMatch(m, i));
      const result: MatchesFetchResult = {
        matches: ms,
        activeCount:
          num(r?.active_count) ||
          ms.filter((m) => m.matchStatus === 'active').length,
        totalCount: num(r?.total_count) || ms.length,
      };
      if (ms.length === 0 && tries > 0) return attempt(tries - 1);
      return result;
    };

    this._matchPromise = attempt(2)
      .then((result) => {
        if (result.matches.length) this._matchCache = result;
        this._matchPromise = null;
        return result;
      })
      .catch((e) => {
        this._matchPromise = null;
        throw e;
      });
    return this._matchPromise;
  }

  /** Fetch a fresh deck — call once the attendee has worked through all their
   *  current matches (Network "all caught up"). */
  async regenerateMatches(): Promise<MatchesFetchResult> {
    this._matchCache = null;
    this._matchPromise = null;
    return this.getMatches({ force: true });
  }

  async getMoreMatches(): Promise<MatchView[]> {
    const r = await this._api.post<{ matches?: unknown[] }>(
      ENDPOINTS.matchesGenerate,
      { force: true, limit: 5 }
    );
    return (r?.matches ?? []).map((m, i) => adaptMatch(m, i));
  }

  clearMatchCache(): void {
    this._matchCache = null;
    this._matchPromise = null;
  }

  clearExhibitorCache(): void {
    this._exhibitorCache.clear();
    this._exhibitorPromise.clear();
  }

  // ── attendee register + profile (verified contract) ───────────────────────
  /** Initial call after auth: `POST /api/attendee/register` creates/resumes the
   *  attendee for the event and returns `profileStatus` — the "profile filled?"
   *  check. The email IS the user id. */
  register(email = this.userId): Promise<Record<string, unknown>> {
    return this._api.post<Record<string, unknown>>(ENDPOINTS.register, {
      event_id: this.eventId,
      email,
    });
  }

  /** Cached + de-duped. Fetches `GET /api/attendee/profile` for identity +
   *  preferences; falls back to `register` when the profile route is unavailable
   *  (e.g. attendee not yet provisioned). Sets the shared `attendeeProfile` /
   *  `enriched` signals so Home / Network / Profile branch consistently. */
  async getProfile(
    opts: { force?: boolean } = {}
  ): Promise<AttendeeProfile | null> {
    if (!opts.force && this.profileLoaded()) return this.attendeeProfile();
    if (this._profilePromise) return this._profilePromise;
    this._profilePromise = (async () => {
      try {
        const raw = await this._api.get<Record<string, unknown>>(
          ENDPOINTS.profile
        );
        const p = adaptProfileResponse(raw, this.userId);
        this.attendeeProfile.set(p);
        this.profileLoaded.set(true);
        return p;
      } catch {
        try {
          const reg = await this.register();
          const p = adaptProfileFromRegister(reg, this.userId);
          this.attendeeProfile.set(p);
          this.profileLoaded.set(true);
          return p;
        } catch {
          this.attendeeProfile.set(null);
          this.profileLoaded.set(true);
          return null;
        }
      } finally {
        this._profilePromise = null;
      }
    })();
    return this._profilePromise;
  }

  /** Reset cached profile (after enrichment) so the next read reflects new prefs. */
  clearProfileCache(): void {
    this.attendeeProfile.set(null);
    this.profileLoaded.set(false);
    this._profilePromise = null;
  }

  /** Login gate. `register` only returns an identity for an email that is on the
   *  event's attendee roster; an unknown email comes back 404 / `{error}` ("Attendee
   *  not found for this email and event"). So: an identity-bearing 200 = provisioned,
   *  a 404 / error = not on the roster. Returns:
   *    • true  → provisioned attendee (proceed; caller then branches on enriched)
   *    • false → unknown email / 400 / 401 / 403 / 404 (blocked, show "no account")
   *    • null  → transient error (don't block a possibly-real user). */
  async attendeeExists(id: string): Promise<boolean | null> {
    try {
      const r = await this.register(id);
      if (r['error']) return false;
      return !!(
        r['userId'] ||
        r['email'] ||
        r['resumed'] === true ||
        r['profileStatus']
      );
    } catch (e) {
      const status = (e as { status?: number })?.status;
      if (status === 400 || status === 401 || status === 403 || status === 404)
        return false;
      return null;
    }
  }

  /** Registered attendee's enriched flag — drives the post-login branch (enriched →
   *  matches; not enriched → intake). Reads the authoritative `profileStatus` from
   *  register. Returns false when unknown/unreachable (caller has already gated). */
  async attendeeEnriched(id: string): Promise<boolean> {
    try {
      const r = await this.register(id);
      return String(r['profileStatus'] ?? '').toLowerCase() === 'enriched';
    } catch {
      return false;
    }
  }

  /** Wipe every per-user cache + handoff signal so a NEW (or cleared) session can
   *  never show the previous user's data. Called on login and logout. */
  resetForNewSession(): void {
    this._matchCache = null;
    this._matchPromise = null;
    this.clearExhibitorCache();
    this.attendeeProfile.set(null);
    this.profileLoaded.set(false);
    this._profilePromise = null;
    this.eventConfig.set(null);
    this._eventPromise = null;
    this.viewedMatch.set(null);
  }

  /** Initialize the intake chat — `POST /api/attendee/chat/init`. The server owns
   *  the transcript; this returns the assistant's opening turn (message + buttons). */
  async chatInit(): Promise<ChatTurn> {
    const r = await this._api.post<Record<string, unknown>>(
      ENDPOINTS.chatInit,
      {}
    );
    return adaptTurn(r);
  }

  /** Send one intake turn — `POST /api/attendee/chat` with `{ message }` ONLY
   *  (server owns the transcript). Returns the assistant's reply + quick-reply
   *  `buttons` + a `complete` flag (auto-enrichment runs on the final turn). */
  async chat(message: string): Promise<ChatTurn> {
    const r = await this._api.post<Record<string, unknown>>(ENDPOINTS.chat, {
      message,
    });
    return adaptTurn(r);
  }

  /** Commit the intake → enriched profile (`/api/attendee/enrich`). The server
   *  owns the transcript, so an empty body enriches from the stored intake; pass
   *  an explicit transcript only for the edit flow. Then re-fetch the canonical
   *  profile so the shared `attendeeProfile`/`enriched` signals update and EVERY
   *  screen switches to the enriched UI; matches are refreshed too. */
  async enrich(
    transcript?: { role: string; content: string }[]
  ): Promise<AttendeeProfile | null> {
    try {
      const body = transcript && transcript.length ? { transcript } : {};
      await this._api.post<Record<string, unknown>>(ENDPOINTS.enrich, body);
    } catch {
      /* best-effort: still refresh the profile below */
    }
    this.clearProfileCache();
    this.clearMatchCache();
    return this.getProfile({ force: true });
  }

  /** Best-effort attendee identity update (name / organisation / role). POSTs to
   *  `/api/attendee/profile` and degrades gracefully (never throws): the caller
   *  persists the same values locally so the UI reflects them immediately, and the
   *  cached `attendeeProfile` signal is patched so any screen reading it stays in
   *  sync. */
  async updateProfile(patch: {
    name?: string;
    organisation?: string;
    role?: string;
  }): Promise<boolean> {
    let ok = false;
    try {
      await this._api.post<Record<string, unknown>>(ENDPOINTS.profile, {
        name: patch.name,
        company: patch.organisation,
        role: patch.role,
      });
      ok = true;
    } catch {
      /* no such endpoint yet — local persistence below keeps the UI honest */
    }
    const current = this.attendeeProfile();
    if (current) {
      this.attendeeProfile.set({
        ...current,
        name: patch.name ?? current.name,
        company: patch.organisation ?? current.company,
        role: patch.role ?? current.role,
      });
    }
    return ok;
  }

  /** Record a match interaction — `PATCH /api/matches/{matchId}/action`. */
  matchAction(
    matchId: string,
    action: string,
    ignoreReason?: string
  ): Promise<unknown> {
    const body: Record<string, unknown> = { action };
    if (ignoreReason) body['ignore_reason'] = ignoreReason;
    return this._api.patch(ENDPOINTS.matchAction(matchId), body);
  }

  // ── event + sessions (show-winners: /api/events/{expoId}) ──────────────────
  /** Cached + de-duped: first caller fetches, everyone else (and the TopBar)
   *  reads the shared `eventConfig` signal — no placeholder-then-real flash. */
  /** Full exhibitor roster for the event — RAM cache keyed by expoId, de-duped
   *  in-flight. Response may be a bare array or `{ exhibitors: [] }`. */
  async getEventExhibitors(
    expoId = this.eventId
  ): Promise<ExhibitorDirectoryView[]> {
    const cached = this._exhibitorCache.get(expoId);
    if (cached) return cached;
    const inflight = this._exhibitorPromise.get(expoId);
    if (inflight) return inflight;

    const attempt = this._api
      .get<unknown>(ENDPOINTS.eventExhibitors(expoId))
      .then((r) => {
        const list = exhibitorListFromResponse(r).map((x, i) =>
          adaptExhibitorDirectory(x, i)
        );
        this._exhibitorCache.set(expoId, list);
        this._exhibitorPromise.delete(expoId);
        return list;
      })
      .catch(() => {
        this._exhibitorPromise.delete(expoId);
        return [] as ExhibitorDirectoryView[];
      });

    this._exhibitorPromise.set(expoId, attempt);
    return attempt;
  }

  async getEventConfig(expoId: string): Promise<EventConfig | null> {
    if (this.eventConfig()) return this.eventConfig();
    if (this._eventPromise) return this._eventPromise;
    this._eventPromise = this._api
      .get<Record<string, unknown>>(ENDPOINTS.events(expoId))
      .then((r) => {
        const e = adaptEvent(r, expoId);
        if (e) this.eventConfig.set(e);
        this._eventPromise = null;
        return e;
      })
      .catch(() => {
        this._eventPromise = null;
        return null;
      });
    return this._eventPromise;
  }

  async getSessions(expoId: string): Promise<SessionView[]> {
    try {
      const r = await this._api.get<{ sessions?: unknown[] }>(
        `${ENDPOINTS.sessions}?expo_id=${encodeURIComponent(expoId)}`
      );
      return (r?.sessions ?? []).map((s, i) => adaptSession(s, i));
    } catch {
      return [];
    }
  }

  async getSession(id: string): Promise<SessionView | null> {
    try {
      const r = await this._api.get<Record<string, unknown>>(
        `${ENDPOINTS.sessions}/${encodeURIComponent(id)}`
      );
      return adaptSession(r, 0);
    } catch {
      return null;
    }
  }

  // ── live ───────────────────────────────────────────────────────────────────
  async getLiveState(sessionId: string): Promise<{
    status: string;
    transcript: { speaker: string; text: string }[];
    insights: { id: string; text: string }[];
  } | null> {
    try {
      const r = await this._api.get<Record<string, unknown>>(
        ENDPOINTS.liveState(sessionId)
      );
      return {
        status: str(r?.['status']) || 'listening',
        transcript: Array.isArray(r?.['transcript'])
          ? (r['transcript'] as unknown[]).map((t) => {
              const rec = (t ?? {}) as Record<string, unknown>;
              return { speaker: str(rec['speaker']), text: str(rec['text']) };
            })
          : [],
        insights: Array.isArray(r?.['insights'])
          ? (r['insights'] as unknown[]).map((x, i) => {
              const rec = (x ?? {}) as Record<string, unknown>;
              return { id: str(rec['id']) || `i-${i}`, text: str(rec['text']) };
            })
          : [],
      };
    } catch {
      return null;
    }
  }

  // ── cross-app connect flow ─────────────────────────────────────────────────
  /** Attendee → request to connect with an exhibitor (with a Calendly slot/note). */
  createConnectRequest(
    exhibitorId: string,
    body: { slot?: string; note?: string } = {}
  ): Promise<unknown> {
    return this._api.post(ENDPOINTS.requests, { exhibitorId, ...body });
  }
  /** Exhibitor → list incoming connect requests. */
  getIncomingRequests(): Promise<{ requests?: unknown[] }> {
    return this._api.get<{ requests?: unknown[] }>(ENDPOINTS.requests);
  }
  /** Exhibitor → accept; backend creates an active connection + notifies attendee. */
  acceptRequest(id: string): Promise<unknown> {
    return this._api.patch(
      `${ENDPOINTS.requests}/${encodeURIComponent(id)}/accept`,
      {}
    );
  }
  rejectRequest(id: string): Promise<unknown> {
    return this._api.patch(
      `${ENDPOINTS.requests}/${encodeURIComponent(id)}/reject`,
      {}
    );
  }
  /** Attendee → accepted + pending connections (Connections screen). */
  async getConnections(): Promise<ConnectionView[]> {
    try {
      const r = await this._api.get<{ connections?: unknown[] }>(
        ENDPOINTS.connections
      );
      return (r?.connections ?? []).map((c, i) => adaptConnection(c, i));
    } catch {
      return [];
    }
  }
}

/* ── adapters ────────────────────────────────────────────────────────────── */

function str(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}
function arr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
}
/** Normalize a suggestions/quick-replies field that may be strings or objects. */
function chipList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => {
      if (typeof x === 'string') return x;
      const o = (x ?? {}) as Record<string, unknown>;
      return (
        str(o['label']) || str(o['text']) || str(o['value']) || str(o['title'])
      );
    })
    .filter((s) => !!s)
    .slice(0, 6);
}
function num(v: unknown): number {
  return typeof v === 'number' ? v : 0;
}
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
function domainOf(url: string): string {
  if (!url) return '';
  try {
    return new URL(
      /^https?:\/\//i.test(url) ? url : `https://${url}`
    ).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}
function titleCase(s: string): string {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) =>
      /[A-Z]{2,}/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(' ');
}
/** A short 2–4 word descriptor that fits a tile (no ellipsis). */
function shortDescriptor(
  caps: string[],
  macros: string[],
  fallback: string
): string {
  let base = caps[0] || macros[0] || fallback || 'Exhibitor';
  const words = base.split(/\s+/).filter(Boolean);
  if (words.length > 4) base = words.slice(0, 4).join(' ');
  return titleCase(base);
}

function parseProductsServices(v: unknown): string[] {
  if (Array.isArray(v)) return arr(v);
  if (typeof v === 'string') {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? arr(parsed) : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Merge structured_data + enrichment; enrichment wins when a key is present. */
function exhibitorEnrichment(ex: Record<string, unknown>): {
  core_capabilities: string[];
  macro_categories: string[];
  target_audience_tags: string[];
  company_summary: string;
} {
  const sd = (ex['structured_data'] ?? {}) as Record<string, unknown>;
  const en = (ex['enrichment'] ?? {}) as Record<string, unknown>;
  const pickArr = (key: string): string[] => {
    if (en[key] !== undefined) return arr(en[key]);
    return arr(sd[key]);
  };
  return {
    core_capabilities: pickArr('core_capabilities'),
    macro_categories: pickArr('macro_categories'),
    target_audience_tags: pickArr('target_audience_tags'),
    company_summary: str(en['company_summary']) || str(sd['company_summary']),
  };
}

function exhibitorSocials(
  meta: Record<string, unknown>
): SocialLinks | undefined {
  const socials: SocialLinks = {
    linkedin: str(meta['linkedin']) || undefined,
    facebook: str(meta['facebook']) || undefined,
    instagram: str(meta['instagram']) || undefined,
    youtube: str(meta['youtube']) || undefined,
  };
  return Object.values(socials).some(Boolean) ? socials : undefined;
}

/** Live API returns `{ exhibitors: [] }`; tolerate a bare array too. */
function exhibitorListFromResponse(r: unknown): unknown[] {
  if (Array.isArray(r)) return r;
  const rec = (r ?? {}) as Record<string, unknown>;
  return Array.isArray(rec['exhibitors']) ? rec['exhibitors'] : [];
}

function adaptExhibitorDirectory(raw: unknown, i = 0): ExhibitorDirectoryView {
  const rec = (raw ?? {}) as Record<string, unknown>;
  const meta = (rec['metadata'] ?? {}) as Record<string, unknown>;
  const enrich = exhibitorEnrichment(rec);
  const name =
    str(rec['companyName']) ||
    str(rec['name']) ||
    str(rec['company']) ||
    'Exhibitor';
  const website = str(rec['website']);
  const domain = domainOf(website);
  const booth = str(rec['boothId']) || str(rec['booth_number']) || null;
  const email = str(rec['email']);
  const bookingProvider = str(meta['meeting_integration']);
  const meetingLink = str(meta['meeting_link']);
  return {
    id: str(rec['id']) || str(rec['exhibitorId']) || `ex-${i}`,
    name,
    booth,
    description: str(rec['description']),
    tagline: shortDescriptor(
      enrich.core_capabilities,
      enrich.macro_categories,
      str(rec['category'])
    ),
    website,
    domain,
    logo:
      str(rec['logo']) ||
      str(meta['logo']) ||
      (domain ? `https://logo.clearbit.com/${domain}` : ''),
    initials: initialsOf(name),
    booking: isCalComBookingEligible(bookingProvider, meetingLink)
      ? meetingLink
      : '',
    bookingProvider,
    socials: exhibitorSocials(meta),
    productsServices: parseProductsServices(rec['productsServices']),
    companySummary: enrich.company_summary,
    capabilities: enrich.core_capabilities,
    categories: enrich.macro_categories,
    audience: enrich.target_audience_tags,
    contactEmail: email || undefined,
    enriched: rec['enriched'] === true,
  };
}

function adaptMeetingDetails(raw: unknown): MeetingDetailsView | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const startTime = str(rec['startTime']) || null;
  const endTime = str(rec['endTime']) || null;
  const status = str(rec['status']) || null;
  if (!startTime && !endTime && !status) return null;
  return {
    status,
    startTime,
    endTime,
    attendeeTimeZone: str(rec['attendeeTimeZone']) || undefined,
    isVirtual: rec['isVirtual'] === true,
    locationType: str(rec['locationType']) || undefined,
    joinUrl: str(rec['joinUrl']) || undefined,
    title: str(rec['title']) || undefined,
  };
}

function adaptMatch(m: unknown, i = 0): MatchView {
  const rec = (m ?? {}) as Record<string, unknown>;
  const ex = (rec['exhibitor'] ?? {}) as Record<string, unknown>;
  const enrich = exhibitorEnrichment(ex);
  const meta = (ex['metadata'] ?? {}) as Record<string, unknown>;
  const caps = enrich.core_capabilities;
  const macros = enrich.macro_categories;
  const audience = enrich.target_audience_tags;
  const name = str(ex['name']) || str(ex['company']) || 'Exhibitor';
  const website =
    str(rec['website_url']) || str(ex['website']) || str(ex['website_url']);
  const domain = domainOf(website);
  const bookingProvider = str(meta['meeting_integration']);
  const meetingLink = str(meta['meeting_link']);
  const booking = isCalComBookingEligible(bookingProvider, meetingLink)
    ? meetingLink
    : '';
  const boothRaw =
    str(ex['booth_number']) || str(ex['boothId']) || str(rec['booth_number']);
  const email = str(ex['email']);
  return {
    id: str(ex['id']) || `m-${i}`,
    matchId: str(rec['match_id']) || str(rec['id']) || null,
    matchStatus: str(rec['match_status']) || null,
    name,
    company: str(ex['company']) || str(ex['companyName']),
    tagline: shortDescriptor(caps, macros, str(ex['category'])),
    description: str(ex['description']),
    reason: str(rec['rationale_text']) || str(rec['company_summary']),
    confidenceTier: str(rec['confidence_tier']) || null,
    matchScore: Math.round(num(rec['match_score']) * 100),
    capabilities: caps,
    booth: boothRaw || null,
    website,
    booking,
    bookingProvider,
    logo:
      str(meta['logo']) ||
      str(ex['logo']) ||
      (domain ? `https://logo.clearbit.com/${domain}` : ''),
    domain,
    initials: initialsOf(name),
    companySummary: enrich.company_summary,
    categories: macros,
    audience,
    productsServices: parseProductsServices(ex['productsServices']),
    contactEmail: email || undefined,
    meetingDetails: adaptMeetingDetails(rec['meeting_details']),
    socials: exhibitorSocials(meta),
  };
}

/** One assistant turn `{ message, buttons, complete }` from chat/init or chat. */
function adaptTurn(r: Record<string, unknown> | null): ChatTurn {
  const rec = (r ?? {}) as Record<string, unknown>;
  const message =
    str(rec['message']) ||
    str(rec['reply']) ||
    str(rec['content']) ||
    str(rec['response']);
  const sugg =
    rec['buttons'] ??
    rec['suggestions'] ??
    rec['quick_replies'] ??
    rec['options'] ??
    rec['chips'];
  const done =
    rec['complete'] === true ||
    rec['done'] === true ||
    rec['completed'] === true ||
    ['complete', 'completed', 'done', 'enriched'].includes(
      str(rec['status']).toLowerCase()
    );
  return { message, suggestions: chipList(sugg), done };
}

/** Map `GET /api/attendee/profile` — canonical identity + preferences payload. */
function adaptProfileResponse(
  raw: Record<string, unknown>,
  userId: string
): AttendeeProfile {
  const sd = (raw['structured_data'] ?? {}) as Record<string, unknown>;
  const status =
    str(raw['profile_status']) ||
    str(raw['profileStatus']) ||
    str(raw['status']);
  const enriched =
    status.toLowerCase() === 'enriched' ||
    status.toLowerCase() === 'complete' ||
    raw['enriched'] === true;
  return {
    id: str(raw['id']) || str(raw['email']) || userId,
    name: str(raw['name']),
    company: str(raw['company']),
    role: str(raw['title']) || str(raw['role']) || str(sd['role']),
    enriched,
    summary: str(sd['summary']) || str(raw['summary']),
    painPoints: arr(sd['pain_points']),
    solutionRequirements: arr(sd['solution_requirements']),
    interests: arr(sd['interests']).length
      ? arr(sd['interests'])
      : arr(raw['interests']),
  };
}

/** Minimal profile from `POST /api/attendee/register` when the profile route fails. */
function adaptProfileFromRegister(
  reg: Record<string, unknown>,
  userId: string
): AttendeeProfile {
  const status =
    str(reg['profileStatus']) ||
    str(reg['profile_status']) ||
    str(reg['status']);
  const enriched =
    status.toLowerCase() === 'enriched' ||
    status.toLowerCase() === 'complete' ||
    reg['enriched'] === true;
  return {
    id: str(reg['userId']) || str(reg['email']) || userId,
    name: str(reg['name']),
    company: str(reg['company']),
    role: str(reg['title']) || str(reg['role']),
    enriched,
    summary: '',
    painPoints: [],
    solutionRequirements: [],
    interests: [],
  };
}

function adaptEvent(
  r: Record<string, unknown> | null,
  expoId: string
): EventConfig | null {
  if (!r) return null;
  const ev = (r['event'] ?? r ?? {}) as Record<string, unknown>;
  const start = str(ev['start_date']) || str(ev['startDate']) || null;
  const end = str(ev['end_date']) || str(ev['endDate']) || null;
  let status: EventConfig['status'] = 'unknown';
  const now = Date.now();
  const s = start ? Date.parse(start) : NaN;
  const e = end ? Date.parse(end) : NaN;
  if (!Number.isNaN(s) && !Number.isNaN(e)) {
    status = now < s ? 'upcoming' : now > e ? 'ended' : 'live';
  }
  return {
    expoId: str(ev['expo_id']) || expoId,
    name: str(ev['name']) || str(ev['event_name']) || '',
    organizer: str(ev['organizer']),
    venue: str(ev['venue']),
    city: str(ev['city']),
    startDate: start,
    endDate: end,
    status,
  };
}

function adaptSession(s: unknown, i = 0): SessionView {
  const rec = (s ?? {}) as Record<string, unknown>;
  const start = str(rec['start_time']) || str(rec['start']) || null;
  const end = str(rec['end_time']) || str(rec['end']) || null;
  let state: SessionView['state'] = 'upcoming';
  const now = Date.now();
  const st = start ? Date.parse(start) : NaN;
  const en = end ? Date.parse(end) : NaN;
  if (!Number.isNaN(st) && !Number.isNaN(en)) {
    state = now > en ? 'past' : now >= st ? 'live' : 'upcoming';
  }
  return {
    id: str(rec['id']) || str(rec['session_id']) || `s-${i}`,
    title: str(rec['title']) || str(rec['name']) || 'Session',
    track: str(rec['track']) || str(rec['category']),
    room: str(rec['room']) || str(rec['location']),
    speakers: arr(rec['speakers']),
    start,
    end,
    state,
    whyMatch: str(rec['why_match']) || str(rec['rationale']),
    summary: str(rec['summary']),
  };
}

function adaptConnection(c: unknown, i = 0): ConnectionView {
  const rec = (c ?? {}) as Record<string, unknown>;
  const ex = (rec['exhibitor'] ?? {}) as Record<string, unknown>;
  const name =
    str(ex['name']) || str(rec['name']) || str(ex['company']) || 'Exhibitor';
  const website = str(ex['website_url']);
  const domain = domainOf(website);
  const status = (str(rec['status']) || 'pending').toLowerCase();
  return {
    id: str(rec['id']) || `c-${i}`,
    exhibitorId: str(ex['id']) || str(rec['exhibitorId']),
    name,
    company: str(ex['company']),
    tagline: str(ex['tagline']) || str(ex['category']),
    logo: domain ? `https://logo.clearbit.com/${domain}` : '',
    initials: initialsOf(name),
    status:
      status === 'active' || status === 'accepted'
        ? 'active'
        : status === 'rejected'
          ? 'rejected'
          : 'pending',
    slot: str(rec['slot']),
    note: str(rec['note']),
  };
}
